// =============================================================================
// FILE STATUS HEADER  (lihat /app/memory/MASTER_BLUEPRINT.md A7)
// status   : ACTIVE (Supabase migration V2 - PREVIEW ONLY)
// protected: NO  -> Halaman Batalkan Transaksi Penjualan (BTJ).
// =============================================================================
// Batalkan Transaksi Penjualan (dokumen alur wewenang):
// - Staff Store: pilih 1 invoice yang MASIH boleh dibatalkan -> pilih alasan (daftar tetap)
//   + keterangan WAJIB -> ajukan. Barang kembali ke store saat disetujui.
// - Kepala Cabang region sama: setujui / tolak. Konfirmasi persetujuan memperingatkan
//   PERMANEN (barang kembali ke store, invoice batal, penagihan mengecualikannya).
// - Owner: read-only (bertindak via Assist Mode). Region ditegakkan backend.
import React, { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Ban, Loader2, Send, Check, RefreshCw, AlertTriangle, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { useV2Auth } from "@/v2/V2AuthContext";
import { EmptyState, ListSkeleton } from "@/v2/components/ux";

const STORE = ["STAFF_STORE_AREMA", "STAFF_STORE_BORNEO"];
const KACAB = ["KACAB_SBY", "KACAB_SMD"];

// R5: daftar tetap alasan (harus sama dgn backend REASON_CODES).
const REASON_OPTIONS = [
  { code: "SALAH_JENIS_TRANSAKSI", label: "Salah jenis transaksi" },
  { code: "SALAH_BARANG", label: "Salah barang" },
  { code: "SALAH_DISKON", label: "Salah diskon" },
  { code: "PEMBELI_BATAL", label: "Pembeli batal" },
];
const REASON_LABEL = Object.fromEntries(REASON_OPTIONS.map((r) => [r.code, r.label]));

const STATUS_LABEL = { DIAJUKAN: "Menunggu Persetujuan", DISETUJUI: "Disetujui", DITOLAK: "Ditolak" };
const STATUS_VARIANT = { DIAJUKAN: "secondary", DISETUJUI: "default", DITOLAK: "destructive" };
const fmtTs = (s) => { if (!s) return "-"; try { return new Date(s).toLocaleString("id-ID", { dateStyle: "short", timeStyle: "short" }); } catch { return s; } };
const rupiah = (n) => "Rp " + (Number(n || 0)).toLocaleString("id-ID");

export default function BatalkanTransaksiPage() {
  const { me, api } = useV2Auth();
  const role = me?.role;
  const canSubmit = STORE.includes(role);
  const canDecide = KACAB.includes(role);

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);

  // Form pengajuan (Staff Store)
  const [invoices, setInvoices] = useState([]);
  const [invId, setInvId] = useState("");
  const [reasonCode, setReasonCode] = useState("");
  const [reasonNote, setReasonNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Dialog keputusan (Kacab)
  const [decision, setDecision] = useState(null); // { doc, mode: 'approve'|'reject' }
  const [decisionNote, setDecisionNote] = useState("");
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadDocs = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api("/api/v2/sale-cancellations");
      const d = await r.json();
      setDocs(Array.isArray(d) ? d : []);
    } catch { toast.error("Gagal memuat daftar dokumen"); }
    setLoading(false);
  }, [api]);

  const loadInvoices = useCallback(async () => {
    if (!canSubmit) return;
    try {
      const r = await api("/api/v2/sale-cancellations/invoices");
      const d = await r.json();
      setInvoices(Array.isArray(d) ? d : []);
    } catch { /* noop */ }
  }, [api, canSubmit]);

  useEffect(() => { loadDocs(); loadInvoices(); }, [loadDocs, loadInvoices]);

  const selectedInv = invoices.find((i) => i.invoice_id === invId);

  const submit = async () => {
    if (!invId) return toast.warning("Pilih transaksi dulu");
    if (!reasonCode) return toast.warning("Pilih alasan pembatalan");
    if (!reasonNote.trim()) return toast.warning("Keterangan alasan WAJIB diisi");
    setSubmitting(true);
    try {
      const r = await api("/api/v2/sale-cancellations", {
        method: "POST",
        body: JSON.stringify({ invoice_id: invId, reason_code: reasonCode, reason_note: reasonNote.trim() }),
      });
      const d = await r.json();
      if (!r.ok) { toast.error(typeof d?.detail === "string" ? d.detail : "Gagal mengajukan"); }
      else {
        toast.success(`Diajukan: ${d.doc_no}`);
        setInvId(""); setReasonCode(""); setReasonNote("");
        loadDocs(); loadInvoices();
      }
    } catch { toast.error("Gagal mengajukan"); }
    setSubmitting(false);
  };

  const openDecision = (doc, mode) => { setDecision({ doc, mode }); setDecisionNote(""); setAck(false); };

  const confirmDecision = async () => {
    if (!decision) return;
    const { doc, mode } = decision;
    if (mode === "approve" && !ack) return toast.warning("Centang konfirmasi bahwa Anda memahami tindakan ini permanen");
    setBusy(true);
    try {
      const r = await api(`/api/v2/sale-cancellations/${doc.id}/${mode}`, {
        method: "POST",
        body: JSON.stringify(mode === "reject" ? { note: decisionNote.trim() || null } : {}),
      });
      const d = await r.json();
      if (!r.ok) {
        const det = d?.detail;
        toast.error(typeof det === "string" ? det : (det?.message || "Gagal memproses"));
      } else if (mode === "approve") {
        const res = d.result || {};
        toast.success(`Disetujui: ${res.reversed ?? 0} barang kembali ke store, invoice dibatalkan`);
        setDecision(null); loadDocs();
      } else { toast.success("Ajuan ditolak"); setDecision(null); loadDocs(); }
    } catch { toast.error("Gagal memproses"); }
    setBusy(false);
  };

  return (
    <div className="space-y-6" data-testid="batalkan-transaksi-page">
      <div className="flex items-center gap-3">
        <Ban className="w-6 h-6 text-destructive" />
        <div>
          <h1 className="text-2xl font-semibold">Batalkan Transaksi</h1>
          <p className="text-sm text-muted-foreground">
            Membatalkan transaksi penjualan yang salah input — lewat dokumen resmi berjenjang.
            Barang kembali ke store dan penagihan otomatis mengecualikannya.
          </p>
        </div>
      </div>

      {/* ============ FORM PENGAJUAN (Staff Store) ============ */}
      {canSubmit && (
        <Card className="p-5 space-y-4 rounded-2xl" data-testid="bt-submit-card">
          <h2 className="text-lg font-medium">Ajukan Pembatalan</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Transaksi (yang masih boleh dibatalkan)</Label>
              <Select value={invId} onValueChange={setInvId}>
                <SelectTrigger data-testid="bt-invoice-select"><SelectValue placeholder="Pilih transaksi penjualan" /></SelectTrigger>
                <SelectContent>
                  {invoices.length === 0 && <div className="px-3 py-2 text-sm text-muted-foreground">Tidak ada transaksi yang bisa dibatalkan</div>}
                  {invoices.map((i) => (
                    <SelectItem key={i.invoice_id} value={i.invoice_id} data-testid={`bt-inv-opt-${i.invoice_no}`}>
                      {i.invoice_no} — {rupiah(i.total)} ({i.unit_count} barang){i.credit_sale ? " · Bayar Nanti" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Alasan (wajib pilih)</Label>
              <Select value={reasonCode} onValueChange={setReasonCode}>
                <SelectTrigger data-testid="bt-reason-select"><SelectValue placeholder="Pilih alasan" /></SelectTrigger>
                <SelectContent>
                  {REASON_OPTIONS.map((r) => (
                    <SelectItem key={r.code} value={r.code} data-testid={`bt-reason-opt-${r.code}`}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Keterangan alasan (wajib isi)</Label>
            <Textarea value={reasonNote} onChange={(e) => setReasonNote(e.target.value)} rows={2}
                      placeholder="mis. Salah pilih size saat input, pembeli minta ganti" data-testid="bt-note-input" />
          </div>
          {selectedInv && (
            <div className="rounded-lg bg-muted p-3 text-sm" data-testid="bt-selected-summary">
              Transaksi <b>{selectedInv.invoice_no}</b> · {selectedInv.unit_count} barang akan dikembalikan ke store bila disetujui Kepala Cabang.
            </div>
          )}
          <div className="flex justify-end">
            <Button onClick={submit} disabled={submitting || !invId || !reasonCode || !reasonNote.trim()} data-testid="bt-submit-btn">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Ajukan Pembatalan
            </Button>
          </div>
        </Card>
      )}

      {/* ============ DAFTAR DOKUMEN ============ */}
      <Card className="p-5 space-y-4 rounded-2xl" data-testid="bt-list-card">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium">Dokumen Batalkan Transaksi</h2>
          <Button variant="outline" size="sm" onClick={loadDocs} data-testid="bt-refresh"><RefreshCw className="w-4 h-4 mr-2" />Muat ulang</Button>
        </div>
        {loading ? <ListSkeleton rows={4} /> : docs.length === 0 ? (
          <EmptyState icon={Ban} title="Belum ada dokumen" description="Ajuan pembatalan transaksi akan muncul di sini." testid="bt-empty" />
        ) : (
          <div className="overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>No. Dokumen</TableHead><TableHead>Invoice</TableHead>
                  <TableHead>Status</TableHead><TableHead>Alasan</TableHead>
                  <TableHead>Pengaju</TableHead><TableHead>Tgl</TableHead><TableHead className="text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {docs.map((d) => (
                  <TableRow key={d.id} data-testid={`bt-row-${d.doc_no}`}>
                    <TableCell className="font-medium">{d.doc_no}</TableCell>
                    <TableCell>{d.invoice_no || "-"}<div className="text-xs text-muted-foreground">{d.store}</div></TableCell>
                    <TableCell><Badge variant={STATUS_VARIANT[d.status] || "secondary"} data-testid={`bt-status-${d.doc_no}`}>{STATUS_LABEL[d.status] || d.status}</Badge></TableCell>
                    <TableCell className="text-xs">
                      <div>{REASON_LABEL[d.reason_code] || d.reason_code}</div>
                      <div className="text-muted-foreground">{d.reason_note}</div>
                    </TableCell>
                    <TableCell className="text-xs">{d.requested_by_name || "-"}</TableCell>
                    <TableCell className="text-xs">{fmtTs(d.requested_at)}</TableCell>
                    <TableCell className="text-right space-x-2 whitespace-nowrap">
                      {canDecide && d.status === "DIAJUKAN" && (
                        <>
                          <Button size="sm" onClick={() => openDecision(d, "approve")} data-testid={`bt-approve-${d.doc_no}`}><Check className="w-4 h-4 mr-1" />Setujui</Button>
                          <Button size="sm" variant="destructive" onClick={() => openDecision(d, "reject")} data-testid={`bt-reject-${d.doc_no}`}><Ban className="w-4 h-4 mr-1" />Tolak</Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {/* ============ DIALOG KEPUTUSAN ============ */}
      <Dialog open={!!decision} onOpenChange={(o) => { if (!o) setDecision(null); }}>
        <DialogContent data-testid="bt-decision-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {decision?.mode === "approve" ? <ShieldAlert className="w-5 h-5 text-destructive" /> : <Ban className="w-5 h-5" />}
              {decision?.mode === "approve" ? "Setujui Pembatalan Transaksi" : "Tolak Ajuan"}
            </DialogTitle>
          </DialogHeader>
          {decision && (
            <div className="space-y-4 text-sm">
              <div className="rounded-lg bg-muted p-3 space-y-1">
                <div><span className="text-muted-foreground">Invoice:</span> <b>{decision.doc.invoice_no}</b></div>
                <div><span className="text-muted-foreground">Store:</span> {decision.doc.store}</div>
                <div><span className="text-muted-foreground">Diajukan oleh:</span> {decision.doc.requested_by_name || "-"}</div>
                <div><span className="text-muted-foreground">Alasan:</span> {REASON_LABEL[decision.doc.reason_code] || decision.doc.reason_code} — {decision.doc.reason_note}</div>
              </div>
              {decision.mode === "approve" ? (
                <>
                  <div className="flex gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3">
                    <AlertTriangle className="w-5 h-5 text-destructive shrink-0" />
                    <p data-testid="bt-approve-warning">
                      Anda akan membatalkan transaksi <b>{decision.doc.invoice_no}</b> secara <b>PERMANEN</b>.
                      <b> {decision.doc.proposed_count} barang</b> akan dikembalikan ke store, invoice ditandai batal,
                      dan seluruh laporan serta penagihan akan mengecualikannya.
                      Setelah disetujui, dokumen ini <b>tidak bisa dibatalkan</b>.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox id="bt-ack" checked={ack} onCheckedChange={(v) => setAck(!!v)} data-testid="bt-ack" />
                    <Label htmlFor="bt-ack" className="cursor-pointer">Saya paham tindakan ini permanen dan menyetujui pembatalan transaksi ini.</Label>
                  </div>
                </>
              ) : (
                <div className="space-y-2">
                  <Label>Alasan penolakan (opsional)</Label>
                  <Textarea value={decisionNote} onChange={(e) => setDecisionNote(e.target.value)} rows={2} data-testid="bt-reject-note" />
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecision(null)} disabled={busy}>Batal</Button>
            <Button variant={decision?.mode === "approve" ? "destructive" : "default"} onClick={confirmDecision}
                    disabled={busy || (decision?.mode === "approve" && !ack)} data-testid="bt-confirm-decision">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {decision?.mode === "approve" ? "Setujui & Batalkan Permanen" : "Tolak Ajuan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
